import React, { useEffect, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';
import './PriceBox.css';
import 'bootstrap/dist/css/bootstrap.min.css';

interface PriceBoxProps {
  data: any;
  extraData: any;
}

const timeFrames = ['24h', '6h', '1h', '5m'];

const PriceBox: React.FC<PriceBoxProps> = ({ data, extraData }) => {
  const [priceData, setPriceData] = useState<any>(null);
  const [extraPriceData, setExtraPriceData] = useState<any>(null);
  const [activeTimeFrame, setActiveTimeFrame] = useState<string>('24h');

  useEffect(() => {
    if (data && extraData) {
      setPriceData(data);
      setExtraPriceData(extraData);
    }
  }, [data, extraData]);

  if (!priceData || !extraPriceData) {
    return <div>Loading...</div>;
  }
  
  const renderPriceChange = (change: string | number) => {
    const value = parseFloat(change as string);
    if (value > 999){
      return (
        <div className={`redgreen green`}>
          &gt;999%
        </div>
      );
    }
    const formattedValue = value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const changeClass = value >= 0 ? 'green' : 'red';
    return (
      <div className={`redgreen ${changeClass}`}>
        {formattedValue}%
      </div>
    );
  };

  const formatVolume = (volume: number): string => {
    if (!volume && volume !== 0) return '-';

    if (Math.abs(volume) >= 1_000_000) {
      return (
        (volume / 1_000_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'M'
      );
    } else if (Math.abs(volume) >= 1_000) {
      return (
        (volume / 1_000).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + 'K'
      );
    }
    return volume.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  };

  // Dynamic keys based on activeTimeFrame for main price data
  const totalTransactionKey = `totalTransaction${activeTimeFrame}`;
  const volumeKey = `volume${activeTimeFrame}`;
  const totalBuyKey = `totalBuy${activeTimeFrame}`;
  const totalSellKey = `totalSell${activeTimeFrame}`;

  // --- First Progress Bar: Transaction Ratio ---
  const buyTxns = Number(priceData?.[totalBuyKey]) || 0;
  const sellTxns = Number(priceData?.[totalSellKey]) || 0;
  const totalTxns = buyTxns + sellTxns;
  const buyTxnPercent = totalTxns ? (buyTxns / totalTxns) * 100 : 0;
  const sellTxnPercent = totalTxns ? (sellTxns / totalTxns) * 100 : 0;

  // --- Second Progress Bar: Volume Ratio ---
  const buyVol = extraPriceData?.[activeTimeFrame]?.buy?.volume ?? 0;
  const sellVol = extraPriceData?.[activeTimeFrame]?.sell?.volume ?? 0;
  const totalVol = buyVol + sellVol;
  const buyVolPercent = totalVol ? (buyVol / totalVol) * 100 : 0;
  const sellVolPercent = totalVol ? (sellVol / totalVol) * 100 : 0;

  return (
    <div className="price-box">
      <div className="price-box-top">
        {timeFrames.map((tf) => (
          <span
            key={tf}
            className={activeTimeFrame === tf ? 'active' : ''}
            onClick={() => setActiveTimeFrame(tf)}
            style={{ cursor: 'pointer' }}
          >
            <div className="smaller">{tf}</div>
            {renderPriceChange(priceData[`priceChange${tf}`])}
          </span>
        ))}
      </div>

      <div className="price-box-bottom">
        <div className="left">
          <div>
            <span className="smaller">Txns</span>
            <span>{priceData?.[totalTransactionKey]?.toLocaleString('en-US')}</span>
          </div>
          <div>
            <span className="smaller">Volume</span>
            <span>
              ${priceData?.[volumeKey] && formatVolume(priceData[volumeKey])}
            </span>
          </div>
        </div>
        <div className="right">
          <div className="space">
            <div className='space-inner'>
                <span className="inner-small">
                <div className="smaller">Buys</div>
                <div>{priceData?.[totalBuyKey]?.toLocaleString('en-US')}</div>
                </span>
                <span className="inner-small right-align">
                <div className="smaller">Sells</div>
                <div>{priceData?.[totalSellKey]?.toLocaleString('en-US')}</div>
                </span>
            </div>
            <div>
                <ProgressBar>
                <ProgressBar
                    now={buyTxnPercent}
                    className="buy-bar"
                    key={1}
                />
                <ProgressBar
                    now={sellTxnPercent}
                    className="sell-bar"
                    key={2}
                />
                </ProgressBar>
          </div>
          </div>



          <div className="space">
            <div className='space-inner'>
            <span className="inner-small">
              <div className="smaller">Buy vol</div>
              <div>${formatVolume(buyVol)}</div>
            </span>
            <span className="inner-small right-align">
              <div className="smaller">Sell vol</div>
              <div>${formatVolume(sellVol)}</div>
            </span>
            </div>
            <div>
            <ProgressBar>
              <ProgressBar
                now={buyVolPercent}
                className="buy-bar"
                key={1}
              />
              <ProgressBar
                now={sellVolPercent}
                className="sell-bar"
                key={2}
              />
            </ProgressBar>
          </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PriceBox;
